import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { SvgIconComponent, injectRegisterIcons } from '@ngneat/svg-icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { RouteSegment } from '@core/enums/route-segment.enum';

import { MovablePanelTitleComponent } from '@shared/components/movable-panel-title';
import { MoveablePanelComponent } from '@shared/components/moveable-panel/moveable-panel.component';
import { MultiSelectComponent, MultiSelectOptionViewModel } from '@shared/components/multi-select';
import { PanelLinkComponent } from '@shared/components/panel-link';
import { SiteDateTimeComponent } from '@shared/components/site-date-time';

import { appFaultedV224Icon } from '@icons/faulted-v2-24';
import { appReadyV224Icon } from '@icons/ready-v2-24';

import { DEFAULT_VEHICLE_SOC_SUMMARY_RANGE, VehicleSocSummaryRange } from '../../enums/vehicle-soc-summary-range.enum';
import { VehicleStateOfChargeSummaryMovablePanelLoadingSkeletonComponent } from '../vehicle-state-of-charge-summary-movable-panel-loading-skeleton';
import { VehicleStateOfChargeSummaryMovablePanelViewModel } from './vehicle-state-of-charge-summary-movable-panel.view-model';

const VEHICLE_SOC_SUMMARY_RANGE_OPTION_LABELS: Record<VehicleSocSummaryRange, string> = {
  [VehicleSocSummaryRange.Last7Days]: 'multi-site-dashboard.vehicle-state-of-charge-summary.last-7-days',
  [VehicleSocSummaryRange.Last14Days]: 'multi-site-dashboard.vehicle-state-of-charge-summary.last-14-days',
};

@Component({
  selector: 'app-vehicle-state-of-charge-summary-movable-panel',
  imports: [
    MoveablePanelComponent,
    MovablePanelTitleComponent,
    MultiSelectComponent,
    PanelLinkComponent,
    VehicleStateOfChargeSummaryMovablePanelLoadingSkeletonComponent,
    SvgIconComponent,
    SiteDateTimeComponent,
    FormsModule,
    RouterLink,
    TranslatePipe,
    NgClass,
  ],
  templateUrl: './vehicle-state-of-charge-summary-movable-panel.component.html',
  host: { class: 'flex flex-col' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehicleStateOfChargeSummaryMovablePanelComponent {
  readonly #translateService = inject(TranslateService);

  public vm = input<VehicleStateOfChargeSummaryMovablePanelViewModel>();
  public isLoading = input<boolean>(false);
  // Sourced separately from the store so the date-range select stays populated while the table data
  // (the view model) is being reloaded.
  public range = input<VehicleSocSummaryRange>(DEFAULT_VEHICLE_SOC_SUMMARY_RANGE);
  public rangeChange = output<VehicleSocSummaryRange>();

  public readonly RouteSegment = RouteSegment;

  public readonly rangeOptions = computed((): MultiSelectOptionViewModel<VehicleSocSummaryRange>[] =>
    [VehicleSocSummaryRange.Last7Days, VehicleSocSummaryRange.Last14Days].map((range) => ({
      value: range,
      label: this.#translateService.instant(VEHICLE_SOC_SUMMARY_RANGE_OPTION_LABELS[range]),
    }))
  );

  constructor() {
    injectRegisterIcons([appReadyV224Icon, appFaultedV224Icon]);
  }

  public onRangeChange(range: VehicleSocSummaryRange): void {
    this.rangeChange.emit(range);
  }
}
